<template>
  <teleport to="body">
    <div class="nav-tabs" ref="tabsContainer">
      <!-- Primary Tabs (Always Visible) -->
      <template v-for="tab in visiblePrimaryTabs" :key="tab.name || tab.id">
        <button
          :class="['nav-tab', { active: activeTab === (tab.name || tab.id) }]"
          @click="selectTab(tab.name || tab.id)"
          :title="tab.title || tab.label"
          :aria-label="tab.title || tab.label"
        >
          <span class="tab-icon" v-html="tab.icon"></span>
          <span class="tab-label">{{ tab.label }}</span>
          <span class="tab-highlight" v-if="activeTab === (tab.name || tab.id)"></span>
        </button>
      </template>
      
      <!-- More Actions Dropdown (Secondary Tabs) -->
      <MoreActionsDropdown
        v-if="visibleSecondaryTabs.length > 0"
        :tabs="visibleSecondaryTabs"
        :activeTab="activeTab"
        :projectId="projectId"
        :enableGrouping="enableGrouping"
        @select="selectTab"
      />
    </div>
  </teleport>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
import MoreActionsDropdown from './MoreActionsDropdown.vue';

interface Tab {
  id: string;
  name?: string;
  label: string;
  permission?: string | string[];
  permissions?: string[];  // Alternative property name for clarity
  title?: string;
  icon?: string;
  priority?: 'primary' | 'secondary';
  category?: string;
  badge?: string | number;
  [key: string]: any;
}

interface Props {
  tabs: Tab[];
  activeTab?: string;
  projectId: string | number;
  maxPrimaryTabs?: number;
  enableGrouping?: boolean;
}

interface Emits {
  select: [tabId: string];
}

const props = withDefaults(defineProps<Props>(), {
  activeTab: '',
  maxPrimaryTabs: 5,
  enableGrouping: false
});
const emit = defineEmits<Emits>();
const tabsContainer = ref(null);

// Initialize permissions composable
const { hasPermissionForProject, hasAnyPermissionForProject } = usePermissions();

// Filter tabs based on permissions
const permittedTabs = computed(() => {
  return props.tabs.filter((tab: Tab) => {
    // Get permissions from either 'permission', 'permissions', or both
    const tabPermissions: string[] = [];
    
    if (tab.permission) {
      if (Array.isArray(tab.permission)) {
        tabPermissions.push(...tab.permission);
      } else {
        tabPermissions.push(tab.permission);
      }
    }
    
    if (tab.permissions) {
      tabPermissions.push(...tab.permissions);
    }
    
    // If no permissions are specified, the tab is always visible
    if (tabPermissions.length === 0) return true;
    
    // If only one permission, use single permission check for better performance
    if (tabPermissions.length === 1) {
      const hasPermission = hasPermissionForProject(tabPermissions[0] as any, props.projectId);
      return hasPermission.value;
    }
    
    // If multiple permissions, check if user has any of them
    const hasAnyPermission = hasAnyPermissionForProject(tabPermissions as any[], props.projectId);
    return hasAnyPermission.value;
  });
});

// Split tabs into primary and secondary based on total count and priority
const primaryTabs = computed(() => {
  const totalTabs = permittedTabs.value.length;

  // If total tabs is less than or equal to maxPrimaryTabs, show all as primary (no dropdown needed)
  if (totalTabs <= props.maxPrimaryTabs) {
    return permittedTabs.value;
  }
  
  // Check if tabs have explicit priority set
  const explicitPrimary = permittedTabs.value.filter((tab: Tab) => tab.priority === 'primary');
  if (explicitPrimary.length > 0) {
    // Respect maxPrimaryTabs limit even with explicit priority
    return explicitPrimary.slice(0, props.maxPrimaryTabs);
  }
  
  // Otherwise, take the first N tabs as primary
  return permittedTabs.value.slice(0, props.maxPrimaryTabs);
});

const secondaryTabs = computed(() => {
  const totalTabs = permittedTabs.value.length;
  
  // If total tabs is less than or equal to maxPrimaryTabs, no secondary tabs needed
  if (totalTabs <= props.maxPrimaryTabs) {
    return [];
  }
  
  // Check if tabs have explicit priority set
  const explicitPrimary = permittedTabs.value.filter((tab: Tab) => tab.priority === 'primary');
  const explicitSecondary = permittedTabs.value.filter((tab: Tab) => tab.priority === 'secondary');
  
  if (explicitPrimary.length > 0 || explicitSecondary.length > 0) {
    // Combine: overflow primary tabs + explicit secondary tabs
    const overflowPrimary = explicitPrimary.slice(props.maxPrimaryTabs);
    return [...overflowPrimary, ...explicitSecondary];
  }
  
  // Otherwise, remaining tabs after maxPrimaryTabs are secondary
  return permittedTabs.value.slice(props.maxPrimaryTabs);
});

// Visible tabs (for rendering)
const visiblePrimaryTabs = computed(() => primaryTabs.value);
const visibleSecondaryTabs = computed(() => secondaryTabs.value);

// Debounce utility for resize events
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

// Check if tabs are overflowing and add class if needed
const checkOverflow = () => {
  if (!tabsContainer.value) return;
  
  const container = tabsContainer.value;
  const tabs = container.querySelectorAll('.nav-tab');
  
  // Check for horizontal overflow
  const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
  
  // Check if tabs are too narrow (indicating text truncation)
  let hasTruncation = false;
  let hasTextOverflow = false;
  
  if (tabs.length > 0) {
    const containerWidth = container.clientWidth - (container.offsetWidth - container.clientWidth); // Account for padding
    const availableWidthPerTab = containerWidth / tabs.length;
    
    // If each tab has less than 100px, labels likely get truncated
    hasTruncation = availableWidthPerTab < 100;
    
    // Check if any label text is being truncated
    tabs.forEach((tab: Element) => {
      const label = tab.querySelector('.tab-label');
      if (label) {
        const labelWidth = label.scrollWidth;
        const tabWidth = tab.clientWidth;
        if (labelWidth > tabWidth - 30) { // 40px for icon and padding
          hasTextOverflow = true;
        }
      }
    });
  }
  
  // Check if tabs are wrapping to multiple lines
  let hasWrapping = false;
  if (tabs.length > 1) {
    const firstTabTop = tabs[0].getBoundingClientRect().top;
    const lastTabTop = tabs[tabs.length - 1].getBoundingClientRect().top;
    hasWrapping = Math.abs(lastTabTop - firstTabTop) > 5; // 5px tolerance
  }
  
  const shouldShowIconOnly = hasHorizontalOverflow || hasTruncation || hasWrapping || hasTextOverflow;
  
  if (shouldShowIconOnly) {
    container.classList.add('has-overflow');
    container.classList.add('icon-only');
  } else {
    container.classList.remove('has-overflow');
    container.classList.remove('icon-only');
  }
};

// Scroll active tab into view
const scrollActiveTabIntoView = async () => {
  if (!tabsContainer.value || !props.activeTab) return;
  
  await nextTick();
  const activeTabElement = tabsContainer.value.querySelector('.nav-tab.active');
  if (activeTabElement) {
    // Scroll the tab into view with smooth behavior on small screens
    if (window.innerWidth <= 480) {
      const containerWidth = tabsContainer.value.offsetWidth;
      const tabWidth = activeTabElement.offsetWidth;
      const tabLeft = activeTabElement.offsetLeft;
      //const scrollLeft = tabsContainer.value.scrollLeft;
      
      // Calculate target scroll position to center the tab
      const targetScroll = tabLeft - (containerWidth - tabWidth) / 2;
      
      tabsContainer.value.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }
  }
};

// Select tab and ensure it's visible
const selectTab = (tabName: string) => {
  emit('select', tabName);
  // Wait for UI to update before scrolling
  nextTick(() => {
    scrollActiveTabIntoView();
  });
};

// Watch for changes in active tab or tab list
watch(() => props.activeTab, () => {
  nextTick(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  });
});
watch(() => props.tabs?.length, () => {
  nextTick(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  });
});

onMounted(() => {
  nextTick(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  });
  
  // Debounced resize handler for better performance
  const debouncedResize = debounce(() => {
    setTimeout(checkOverflow, 10);
    scrollActiveTabIntoView();
  }, 150);
  
  window.addEventListener('resize', debouncedResize);
  
  // Clean up event listener on unmount
  onUnmounted(() => {
    window.removeEventListener('resize', debouncedResize);
  });
});
</script>

<style scoped>
/* Use Inter font to match demo */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.quick-actions-container {
  position: static;
  width: 100%;
}

.nav-tabs {
  display: flex;
  gap: 8px;
  background: white;
  border-radius: 16px;
  padding: 8px;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  position: fixed;
  top: calc(var(--top-bar-height, 64px) + var(--trial-banner-offset, 0px));
  left: 50%;
  transform: translateX(-50%);
  z-index: var(--z-sticky, 1020);
  width: calc(100% - 40px);
  max-width: 1400px;
  isolation: isolate;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  height: auto;
  min-height: auto;
}

.nav-tab {
  flex: 1;
  padding: 12px 20px;
  border: none;
  background: transparent;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  outline: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  white-space: nowrap;
  height: auto;
  min-height: auto;
  line-height: 1;
}

.nav-tab:hover {
  background: #f8fafc;
  color: #475569;
}

.nav-tab:focus {
  outline: none;
  box-shadow: none;
}

.nav-tab::after {
  display: none;
}

.nav-tab:hover::after {
  display: none;
}

.nav-tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.nav-tab.active::after {
  display: none;
}

.tab-icon {
  margin-right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;
  flex-shrink: 0;
}

.tab-icon svg {
  height: 18px;
  width: 18px;
  transition: none;
}

/* Tooltip removed to match demo exactly */
.nav-tab::before {
  display: none;
}

.nav-tab:hover::before,
.nav-tab:focus::before,
.nav-tab:focus-visible::before {
  display: none;
}

.active .tab-icon svg {
  transform: none;
  filter: none;
}

.tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: none;
  text-align: center;
  line-height: 1;
  flex-shrink: 1;
}

.active .tab-label {
  transform: none;
  font-weight: 500;
}

.tab-highlight {
  display: none;
}



/* When tabs overflow, hide labels and show only icons */
.nav-tabs.icon-only .tab-label {
  display: none;
}

.nav-tabs.icon-only .nav-tab {
  min-width: 48px;
  padding: 16px 12px;
  flex: 0 0 auto;
}

.nav-tabs.icon-only .tab-icon {
  margin-right: 0;
  height: 22px;
  width: 22px;
}

.nav-tabs.icon-only .tab-icon svg {
  height: 20px;
  width: 20px;
}

/* Enhanced active state for icon-only mode */
.nav-tabs.icon-only .nav-tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.nav-tabs.icon-only .nav-tab.active::after {
  display: none;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .nav-tabs {
    width: calc(100% - 32px);
  }
}

@media (max-width: 992px) {
  .nav-tabs {
    width: calc(100% - 24px);
    padding: 6px;
    gap: 6px;
  }
  
  .nav-tab {
    padding: 14px 20px;
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .nav-tabs {
    width: calc(100% - 20px);
    top: calc(var(--top-bar-height-mobile, 56px) + var(--trial-banner-offset, 0px));
    padding: 6px;
    border-radius: 12px;
    gap: 4px;
  }
  
  .nav-tab {
    padding: 12px 16px;
    font-size: 13px;
  }
  
  .tab-icon {
    height: 18px;
    width: 18px;
  }

  .tab-icon svg {
    height: 16px;
    width: 16px;
  }
}

@media (max-width: 480px) {
  .nav-tabs {
    width: calc(100% - 16px);
    top: calc(var(--top-bar-height-mobile, 56px) + var(--trial-banner-offset, 0px));
    padding: 4px;
    gap: 4px;
    border-radius: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    justify-content: flex-start;
  }
  
  /* Hide scrollbar */
  .nav-tabs::-webkit-scrollbar {
    display: none;
  }
  
  .nav-tab {
    padding: 12px 16px;
    min-width: 44px;
    min-height: 44px;
    flex: 0 0 auto;
    touch-action: pan-x;
  }
  
  .tab-label {
    display: none;
  }
  
  .tab-icon {
    margin-right: 0;
    height: 20px;
    width: 20px;
  }
  
  .tab-icon svg {
    height: 18px;
    width: 18px;
  }

  /* Active state for mobile */
  .nav-tab.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  /* Fade indicator for scrollable content - right edge */
  .nav-tabs::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 20px;
    background: linear-gradient(to right, transparent, white 75%);
    pointer-events: none;
    border-radius: 0 12px 12px 0;
  }

  /* Fade indicator for scrollable content - left edge */
  .nav-tabs::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 20px;
    background: linear-gradient(to left, transparent, white 75%);
    pointer-events: none;
    border-radius: 12px 0 0 12px;
    z-index: 1;
  }
}


</style>
